// ---------------------------------------------------------------------------
// PWA Caisse Tablette — QR/barcode scanner modal
// Uses the useQrScanner hook to manage camera access and code parsing.
// ---------------------------------------------------------------------------

import { useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from '@/store/app-store';
import { useCartStore } from '@/store/cart-store';
import { useTableStore } from '@/store/table-store';
import { useReservationStore } from '@/store/reservation-store';
import { useQrScanner } from '@/hooks/useQrScanner';
import type { ScanResult } from '@/lib/types';

export default function ScannerModal() {
  const scannerOpen = useAppStore((s) => s.scannerOpen);
  const setScannerOpen = useAppStore((s) => s.setScannerOpen);
  const setScreen = useAppStore((s) => s.setScreen);
  const showToast = useAppStore((s) => s.showToast);
  const setTable = useCartStore((s) => s.setTable);
  const setReservation = useCartStore((s) => s.setReservation);
  const selectTable = useTableStore((s) => s.selectTable);
  const searchReservation = useReservationStore((s) => s.search);

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handleScan = useCallback(
    (result: ScanResult) => {
      switch (result.type) {
        case 'reservation': {
          const matches = searchReservation(result.value);
          if (matches.length > 0) {
            const resa = matches[0];
            if (resa.table_id) {
              setTable(resa.table_id);
            }
            setReservation(resa.id);
            showToast(`Reservation ${resa.client_name} chargee`, 'success');
            setScreen('caisse');
          } else {
            showToast('Reservation non trouvee', 'error');
          }
          break;
        }

        case 'table': {
          selectTable(result.value);
          setTable(result.value);
          showToast(`Table ${result.value} selectionnee`, 'success');
          setScreen('tables');
          break;
        }

        case 'ticket': {
          showToast(`Ticket #${result.value} scanne`, 'info');
          setScreen('tickets');
          break;
        }

        case 'client': {
          showToast(`Client ID: ${result.value}`, 'info');
          break;
        }

        case 'product': {
          showToast(`Code-barres: ${result.value}`, 'info');
          break;
        }

        case 'json': {
          // Handle structured QR data
          const data = result.data as Record<string, unknown> | undefined;
          if (data?.type === 'reservation' && data?.id) {
            handleScan({ type: 'reservation', value: String(data.id) });
          } else {
            showToast('QR code lu', 'info');
          }
          break;
        }

        default:
          showToast(`Code non reconnu: ${result.value}`, 'warning');
      }

      setScannerOpen(false);
    },
    [searchReservation, setTable, setReservation, selectTable, showToast, setScreen, setScannerOpen],
  );

  const { startScanning, stopScanning, isScanning, toggleTorch, switchCamera, torchOn } = useQrScanner(videoRef, handleScan);

  // Auto-start scanning when modal opens
  useEffect(() => {
    if (scannerOpen) {
      // Small delay to let the video element mount
      const timer = setTimeout(() => {
        startScanning().catch(() => {
          showToast('Impossible d\'acceder a la camera', 'error');
        });
      }, 300);
      return () => clearTimeout(timer);
    } else {
      stopScanning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerOpen]);

  if (!scannerOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9500] flex flex-col bg-black animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-surface/90 backdrop-blur-sm">
        <h3 className="text-sm font-semibold text-white">Scanner QR / Code-barres</h3>
        <button
          type="button"
          onClick={() => setScannerOpen(false)}
          className="text-gray-400 hover:text-white p-1"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Video area */}
      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          playsInline
          muted
        />

        {/* Scanning overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {/* Scan target box */}
          <div className="w-64 h-64 border-2 border-white/50 rounded-2xl relative">
            {/* Corner markers */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-accent rounded-tl-lg" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-accent rounded-tr-lg" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-accent rounded-bl-lg" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-accent rounded-br-lg" />

            {/* Scan line animation */}
            {isScanning && (
              <div className="absolute left-2 right-2 h-0.5 bg-accent/70 animate-pulse top-1/2" />
            )}
          </div>
        </div>
      </div>

      {/* Footer with controls */}
      <div className="px-4 py-4 bg-surface/90 backdrop-blur-sm">
        {/* Torch + Camera buttons */}
        <div className="flex gap-3 justify-center mb-3">
          <button
            type="button"
            onClick={toggleTorch}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
              torchOn ? 'bg-warning text-black' : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline mr-1 -mt-0.5">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            {torchOn ? 'Flash ON' : 'Flash'}
          </button>
          <button
            type="button"
            onClick={switchCamera}
            className="px-4 py-2 rounded-lg text-xs font-medium bg-surface-card text-gray-300 hover:bg-surface-hover transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline mr-1 -mt-0.5">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Camera
          </button>
        </div>
        <p className="text-gray-400 text-xs mb-2 text-center">
          Placez le QR code ou code-barres dans le cadre
        </p>
        <div className="flex gap-3 justify-center text-[10px] text-gray-500">
          <span className="bg-surface-card px-2 py-1 rounded">RESA:xxx</span>
          <span className="bg-surface-card px-2 py-1 rounded">TABLE:xxx</span>
          <span className="bg-surface-card px-2 py-1 rounded">TICKET:xxx</span>
          <span className="bg-surface-card px-2 py-1 rounded">EAN-13</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
